import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, collectionGroup, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useUnit } from '@/contexts/UnitContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { formatDate, formatCurrency } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/swal';
import { Invoice, Event } from '@/types';
import { PoundSterling, Search, Mail, Download, Filter } from 'lucide-react';

export default function TreasurerDashboardPage() {
  const { currentUser } = useAuth();
  const { currentUnit } = useUnit();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [events, setEvents] = useState<Map<string, Event>>(new Map());
  const [filterStatus, setFilterStatus] = useState<'all' | 'outstanding' | 'partial' | 'paid' | 'voided'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!currentUnit) {
        setLoading(false);
        return;
      }

      try {
        // Get all events for this unit
        const eventsSnapshot = await getDocs(
          collection(db, 'units', currentUnit.id, 'events')
        );

        const eventsMap = new Map<string, Event>();
        const allInvoices: Invoice[] = [];

        for (const eventDoc of eventsSnapshot.docs) {
          const eventData = eventDoc.data();
          const eventDate = eventData.date?.toDate() || new Date();
          eventsMap.set(eventDoc.id, {
            id: eventDoc.id,
            unitId: currentUnit.id,
            name: eventData.name,
            date: eventDate,
            location: eventData.location,
            costPerPerson: eventData.costPerPerson,
            questionnaire: eventData.questionnaire || [],
            maxCapacity: eventData.maxCapacity,
            currentAttendees: eventData.currentAttendees || 0,
            waitlistEnabled: eventData.waitlistEnabled || false,
            waitlist: eventData.waitlist || [],
            rsvpDeadline: eventData.rsvpDeadline?.toDate() || new Date(),
            paymentDeadline: eventData.paymentDeadline?.toDate() || eventDate,
            eventStatus: eventData.eventStatus,
            dresscode: eventData.dresscode,
            customInstructions: eventData.customInstructions,
            isPublic: eventData.isPublic ?? false,
            requirePrefixRankUnitInfo: eventData.requirePrefixRankUnitInfo ?? true,
            createdBy: eventData.createdBy,
            createdAt: eventData.createdAt?.toDate() || new Date(),
          });

          // Get invoices for this event
          const invoicesSnapshot = await getDocs(
            collection(db, 'units', currentUnit.id, 'events', eventDoc.id, 'invoices')
          );

          for (const invoiceDoc of invoicesSnapshot.docs) {
            const invoiceData = invoiceDoc.data();
            if (invoiceData.deletedAt) {
              continue;
            }
            allInvoices.push({
              id: invoiceDoc.id,
              eventId: eventDoc.id,
              unitId: currentUnit.id,
              ownerUid: invoiceData.ownerUid,
              totalAmount: invoiceData.totalAmount,
              amountPaid: invoiceData.amountPaid,
              outstandingBalance: invoiceData.outstandingBalance,
              status: invoiceData.status,
              rsvps: invoiceData.rsvps || [],
              paymentHistory: invoiceData.paymentHistory || [],
              paymentLink: invoiceData.paymentLink,
              remindersSent: invoiceData.remindersSent || [],
              createdAt: invoiceData.createdAt?.toDate() || new Date(),
              updatedAt: invoiceData.updatedAt?.toDate() || new Date(),
            });
          }
        }

        setEvents(eventsMap);
        setInvoices(allInvoices);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        showError('Failed to load invoices');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [currentUnit]);

  const filteredInvoices = invoices.filter((invoice) => {
    if (filterStatus !== 'all' && invoice.status !== filterStatus) {
      return false;
    }
    // Add search filtering if needed
    return true;
  });

  const totalOutstanding = invoices.reduce((sum, inv) => sum + inv.outstandingBalance, 0);
  const totalPaid = invoices.reduce((sum, inv) => sum + inv.amountPaid, 0);
  const outstandingCount = invoices.filter((inv) => inv.status === 'outstanding' || inv.status === 'partial').length;

  const handleSendReminder = async (invoice: Invoice) => {
    if (!currentUnit) return;
    
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const sendReminder = httpsCallable(functions, 'sendManualPaymentReminder');
      
      await sendReminder({
        unitId: currentUnit.id,
        eventId: invoice.eventId,
        invoiceId: invoice.id,
      });
      
      showSuccess('Payment reminder sent successfully');
    } catch (error) {
      console.error('Error sending reminder:', error);
      showError('Failed to send reminder');
    }
  };

  const handleCopyPaymentLink = (paymentLink: string) => {
    navigator.clipboard.writeText(paymentLink);
    showSuccess('Payment link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading treasurer dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Treasurer Dashboard</h1>
            <p className="text-muted-foreground">
              Manage payments and invoices for {currentUnit?.name}
            </p>
          </div>

          {/* Summary Cards */}
          <div className="mb-8 grid gap-6 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
                <PoundSterling className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{invoices.length}</div>
                <p className="text-xs text-muted-foreground">Across all events</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
                <PoundSterling className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">
                  {formatCurrency(totalOutstanding)}
                </div>
                <p className="text-xs text-muted-foreground">{outstandingCount} unpaid</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Collected</CardTitle>
                <PoundSterling className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(totalPaid)}
                </div>
                <p className="text-xs text-muted-foreground">All events</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Collection Rate</CardTitle>
                <PoundSterling className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {invoices.length > 0
                    ? Math.round((totalPaid / (totalPaid + totalOutstanding)) * 100)
                    : 0}%
                </div>
                <p className="text-xs text-muted-foreground">Payment completion</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex gap-2">
                  <Button
                    variant={filterStatus === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus('all')}
                  >
                    All ({invoices.length})
                  </Button>
                  <Button
                    variant={filterStatus === 'outstanding' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus('outstanding')}
                  >
                    Outstanding ({invoices.filter((i) => i.status === 'outstanding').length})
                  </Button>
                  <Button
                    variant={filterStatus === 'partial' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus('partial')}
                  >
                    Partial ({invoices.filter((i) => i.status === 'partial').length})
                  </Button>
                  <Button
                    variant={filterStatus === 'paid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterStatus('paid')}
                  >
                    Paid ({invoices.filter((i) => i.status === 'paid').length})
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoices List */}
          <Card>
            <CardHeader>
              <CardTitle>All Invoices</CardTitle>
              <CardDescription>
                View and manage payment invoices across all events
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredInvoices.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <PoundSterling className="mx-auto mb-4 h-12 w-12 opacity-20" />
                  <p>No invoices found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredInvoices.map((invoice) => {
                    const event = events.get(invoice.eventId);
                    return (
                      <div
                        key={invoice.id}
                        className="flex items-center justify-between rounded-lg border p-4 hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <div className="font-semibold">{event?.name || 'Unknown Event'}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(event?.date || new Date())} • Invoice #{invoice.id.slice(0, 8)}
                          </div>
                          <div className="mt-2 flex items-center gap-4 text-sm">
                            <span>
                              <strong>Total:</strong> {formatCurrency(invoice.totalAmount)}
                            </span>
                            <span className="text-green-600">
                              <strong>Paid:</strong> {formatCurrency(invoice.amountPaid)}
                            </span>
                            {invoice.outstandingBalance > 0 && (
                              <span className="text-amber-600">
                                <strong>Outstanding:</strong> {formatCurrency(invoice.outstandingBalance)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              invoice.status === 'paid' ? 'default' :
                              invoice.status === 'voided' ? 'outline' :
                              invoice.status === 'partial' ? 'secondary' :
                              invoice.status === 'pending_verification' ? 'secondary' :
                              'destructive'
                            }
                            className="capitalize"
                          >
                            {invoice.status}
                          </Badge>

                          {invoice.status !== 'paid' && invoice.status !== 'voided' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendReminder(invoice)}
                              >
                                <Mail className="mr-2 h-4 w-4" />
                                Send Reminder
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCopyPaymentLink(invoice.paymentLink)}
                              >
                                Copy Link
                              </Button>
                            </>
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/invoice/${invoice.id}`)}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

